<template>
  <Loading :loading="isLoading" loading-text="Loading appointment types...">
    <div class="card-layout">
      <div>
        <div class="flex items-center">
          <img src="@/assets/my-calendar/availability/paint.svg" alt="color" />
          <div class="card-title ml-2">Customize Appointment Type</div>
        </div>
        <div class="mt-1 text-[12px] text-[#999999]">
          Appointment types are managed at the practice level. Changes will affect all providers in this practice.
        </div>
      </div>

      <div class="mt-6 flex flex-col gap-3 ">
        <div
          v-for="(eventType, index) in eventTypeList"
          :key="eventType.id"
          :data-testid="`my-calendar.setup-appt-type-card.type-row.${eventType.id}`"
          class="pb-3 flex items-center border-b border-dashed border-[#E5E5E5]"
        >
          <div :data-testid="`my-calendar.setup-appt-type-card.type-name-text.${eventType.id}`" class="color-font">{{ eventType.name }}</div>
          <div
            :data-testid="`my-calendar.setup-appt-type-card.color-swatch-btn.${eventType.id}`"
            class="w-14 h-5 rounded cursor-pointer mr-10"
            :style="{ backgroundColor: eventType.color_hex }"
            @click="openColorPicker(index)"
          ></div>
          <div :data-testid="`my-calendar.setup-appt-type-card.edit-btn.${eventType.id}`" class="action-btn cursor-pointer flex items-center" @click="openColorPicker(index)">
            <img src="@/assets/my-calendar/task/edit.svg" alt="edit" class="mr-1" />
            <span class="text-sm color-[#666666]">Edit</span>
          </div>
          <div
            :data-testid="`my-calendar.setup-appt-type-card.delete-btn.${eventType.id}`"
            :class="[
              'action-btn flex items-center ml-4',
              canDeleteEventType ? 'cursor-pointer' : 'cursor-not-allowed opacity-50 action-btn-disabled'
            ]"
            @click="deleteEventType(index)"
          >
            <img src="@/assets/my-calendar/task/red-delete.svg" alt="delete" class="mr-1" />
            <span class="text-sm color-[#666666]">Delete</span>
          </div>
        </div>

        <ResponsiveButton
          data-testid="my-calendar.setup-appt-type-card.add-new-appointment-type-btn"
          button_type="outlined"
          built_in_theme="dark"
          size="medium"
          display_name="Add New Appointment Type"
          class="w-[260px]"
          @click-button="handleAddEventType"
        />
      </div>

      <ColorPickerDialog
        :name="currentName"
        :color="currentColor"
        :mode="dialogMode"
        :visible="showColorDialog"
        :loading="isDialogLoading"
        @submit="handleDialogSubmit"
        @close="closeColorDialog"
      />

      <DeleteConfirmDialog
        v-model="showDeleteDialog"
        test-id-prefix="my-calendar.setup-appt-type-card.delete-dialog"
        title="Delete Appointment Type?"
        :loading="isDialogLoading"
        :item-name="currentDeleteName"
        @confirm="confirmDelete"
      />
    </div>
  </Loading>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import ColorPickerDialog from './setup-scheduling/ColorPickerDialog.vue';
import DeleteConfirmDialog from '@/views/my-workbench-new/archive/components/DeleteConfirmDialog.vue';
import { notify } from '@/utils/notify';

const store = useStore();

const eventTypeList = computed(() => store.getters['calendar/rawEventTypeList'] || []);
const canDeleteEventType = computed(() => eventTypeList.value.length > 1);

const showColorDialog = ref(false);
const dialogMode = ref('edit');
const currentColorIndex = ref(null);
const currentEventTypeId = ref(null);
const currentName = ref('');
const currentColor = ref('');
const isLoading = ref(false);
const isDialogLoading = ref(false);

const showDeleteDialog = ref(false);
const currentDeleteIndex = ref(null);
const currentDeleteName = computed(() => {
  if (currentDeleteIndex.value === null) return '';
  return eventTypeList.value[currentDeleteIndex.value]?.name || '';
});

/**
 * Open the color picker dialog for a specific event type row.
 * @param {number} index - Index of the event type in the list.
 */
const openColorPicker = (index) => {
  dialogMode.value = 'edit';
  currentColorIndex.value = index;
  currentEventTypeId.value = eventTypeList.value[index].id;
  currentName.value = eventTypeList.value[index].name;
  currentColor.value = eventTypeList.value[index].color_hex;
  showColorDialog.value = true;
  document.body.classList.add('color-picker-active');
};

const closeColorDialog = () => {
  showColorDialog.value = false;
  currentColorIndex.value = null;
  currentEventTypeId.value = null;
  currentName.value = '';
  currentColor.value = '';
  document.body.classList.remove('color-picker-active');
};

const handleAddEventType = () => {
  dialogMode.value = 'create';
  currentColorIndex.value = null;
  currentEventTypeId.value = null;
  currentName.value = '';
  currentColor.value = '#409EFF';
  showColorDialog.value = true;
  document.body.classList.add('color-picker-active');
};

const handleDialogSubmit = async ({ name, color }) => {
  if (!name) {
    notify({
      title: 'Error',
      message: 'Appointment type name is required',
      type: 'error',
    });
    return;
  }
  const submitMode = dialogMode.value;
  const submitEventTypeId = currentEventTypeId.value;
  isDialogLoading.value = true;
  try {
    if (submitMode === 'create') {
      await store.dispatch('calendar/createPracticeEventType', {
        name,
        color_hex: color,
      });
    } else if (submitEventTypeId !== null) {
      await store.dispatch('calendar/updatePracticeEventTypeColor', {
        eventId: parseInt(submitEventTypeId),
        updatePracticeEventTypeColorInput: {
          name,
          color_hex: color,
        }
      });
    }

    await store.dispatch('calendar/fetchPracticeEventTypes');
    closeColorDialog();

  } catch (error) {
    console.error('Error updating appointment type:', error);
  } finally {
    isDialogLoading.value = false;
  }
};

const deleteEventType = (index) => {
  if (!canDeleteEventType.value) return;
  currentDeleteIndex.value = index;
  showDeleteDialog.value = true;
};

const closeDeleteDialog = () => {
  showDeleteDialog.value = false;
  currentDeleteIndex.value = null;
};

const confirmDelete = async () => {
  if (!canDeleteEventType.value) return;
  const eventTypeId = eventTypeList.value[currentDeleteIndex.value]?.id;

  if (!eventTypeId) return;

  isDialogLoading.value = true;
  try {
    await store.dispatch('calendar/deletePracticeEventType', parseInt(eventTypeId));
    await store.dispatch('calendar/fetchPracticeEventTypes');
    closeDeleteDialog();
  } catch (error) {
    console.error('Error deleting appointment type:', error);
  } finally {
    isDialogLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('calendar/fetchPracticeEventTypes');
});
</script>

<style scoped>
.card-layout {
  background-color: #FFFFFF;
  border-radius: 10px;
  padding: 40px;
  font-family: 'Inter';
  box-shadow: 0px 1px 10px 0px rgba(74, 74, 74, 0.08);
}
.card-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
}
.color-font {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
  min-width: 206px;
}
.action-btn {
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
}

.action-btn:hover {
  background-color: #F5F8FC;
}

.action-btn:hover span {
  color: #1E4670;
}

.action-btn-disabled:hover {
  background-color: transparent;
}

.action-btn-disabled:hover span {
  color: #666666;
}
</style>